// Paso "Estamos verificando tu pago", compartido por /pay y /subscribe.
//
// Aparece cuando hay un pago del widget que todavía no pudimos confirmar: se
// agotó el sondeo después del aviso de éxito, o la página se recargó con un
// pago en curso guardado (ver lib/checkout-resume.ts).
//
// La acción principal es VERIFICAR el mismo pago, nunca crear otro. Pagar de
// nuevo existe, pero como salida explícita y apagada: antes el botón principal
// de esta situación era "Reintentar el pago", que descartaba un pago
// posiblemente aprobado y cobraba dos veces.

import { useEffect, useRef } from 'react';
import AuthShell from '@/components/AuthShell';
import SubmitButton from '@/components/SubmitButton';
import { btn } from '@/lib/button-styles';
import { DO_NOT_REPAY_MESSAGE } from '@/lib/checkout-outcome';

// Chequeo de fondo mientras la pantalla está abierta: si el cobro se aprueba,
// el usuario sigue solo sin tener que apretar nada. Espaciado creciente: cubre
// ~10 min (una caida de la pasarela suele durar mas que 2) con las mismas 12
// consultas, en vez de insistir cada 10 s contra su limite por IP.
const BACKGROUND_DELAYS_MS: readonly number[] = [
  10_000, 15_000, 20_000, 30_000, 45_000, 60_000, 60_000, 60_000, 60_000, 60_000, 60_000, 60_000,
];

interface PaymentVerifyingProps {
  /** 'resume': pago retomado tras recargar. 'timeout': se agotó el sondeo. */
  origin: 'resume' | 'timeout';
  /**
   * Segundos que le quedan al intento anterior. Se pasa cuando el backend frenó
   * un checkout nuevo porque el pago anterior todavía puede cobrarse: evita que
   * el usuario crea que "pagar con otra tarjeta" va a funcionar enseguida.
   */
  retryAfterSeconds?: number;
  /** Vuelve a sondear el MISMO pago. */
  onVerify: () => void;
  /** Salida explícita: recién acá se habilita un checkout nuevo. */
  onPayAgain: () => void;
  /** Consulta silenciosa periódica; solo actúa si hay un veredicto definitivo. */
  onBackgroundCheck?: () => void;
}

export default function PaymentVerifying({
  origin,
  retryAfterSeconds,
  onVerify,
  onPayAgain,
  onBackgroundCheck,
}: PaymentVerifyingProps): JSX.Element {
  // El callback cambia en cada render del padre: el intervalo lee siempre el
  // último sin reiniciarse.
  const checkRef = useRef(onBackgroundCheck);
  checkRef.current = onBackgroundCheck;

  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | undefined;
    const schedule = (i: number): void => {
      const delay = BACKGROUND_DELAYS_MS[i];
      if (delay === undefined) return;
      id = setTimeout(() => {
        checkRef.current?.();
        schedule(i + 1);
      }, delay);
    };
    schedule(0);
    return () => clearTimeout(id);
  }, []);

  return (
    <AuthShell title="Estamos verificando tu pago" subtitle="Puede demorar unos minutos.">
      <div className="space-y-4">
        <div role="status" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
          {origin === 'resume'
            ? 'Encontramos un pago que empezaste hace un rato y todavía no está confirmado. '
            : 'Todavía no nos llegó la confirmación del cobro. '}
          <strong>{DO_NOT_REPAY_MESSAGE}</strong>
        </div>
        {retryAfterSeconds !== undefined && (
          <p className="text-xs text-text-muted">
            Para evitar un cobro doble no tomamos otro pago hasta que este termine de
            resolverse (máximo {Math.max(1, Math.ceil(retryAfterSeconds / 60))} minutos).
          </p>
        )}
        <SubmitButton type="button" onClick={onVerify}>
          Verificar de nuevo
        </SubmitButton>
        <button type="button" onClick={onPayAgain} className={btn('ghost', 'md', 'w-full')}>
          Pagar con otra tarjeta
        </button>
      </div>
    </AuthShell>
  );
}
